import React, { useState, useEffect } from 'react';
import { reaction } from 'mobx';
import { getEnv } from 'mobx-state-tree';
import { useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import {
    useUsersGridUIStore,
    useProceduresUIStore,
    useGroupsUIStore,
} from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { useStyles } from './GroupsPage.styles';
import { ThemeProps } from '@styles/theme';
import { Observer } from 'mobx-react';
import { GroupsList } from '../GroupsList';
import { GroupsHeading } from '../GroupsHeading';
import { GroupHeader } from '../GroupHeader';
import { UsersList } from '../UsersList/UsersList';
import { ProceduresList } from '../ProceduresList/ProceduresList';
import { GroupModal } from '../GroupModal';
import { NoGroupSelected } from '../NoGroupSelected';

const GROUP_USERS_LIMIT = 1000;
const GROUP_PROCEDURES_LIMIT = 100;

export const GroupsPage: React.FC = () => {

    const usersGridUIStore = useUsersGridUIStore();
    const proceduresGridUIStore = useProceduresUIStore();
    const groupsUIStore = useGroupsUIStore();

    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isTabletLayout = useBreakpointDown('desktop');
    const isPhoneLayout = useBreakpointDown('mobile');
    const [selectedTab, setSelectedTab] = useState<'users' | 'procedures'>('users');
    const [groupsDrawerOpen, setGroupsDrawerOpen] = useState(false);

    const { groups } = getEnv<IStoresEnv>(usersGridUIStore);

    const setGroupFilter = (inGroup: number, inGroupId?: string) => {
        if(inGroupId){
            usersGridUIStore.setParams({
                page: 0,
                limit: GROUP_USERS_LIMIT,
                inGroup,
                inGroupId: inGroupId,
            });
            proceduresGridUIStore.setParams({
                page: 0,
                limit: GROUP_PROCEDURES_LIMIT,
                inGroup,
                inGroupId: inGroupId,
            });
        }else{
            usersGridUIStore.setParams({
                page: 0,
                limit: GROUP_USERS_LIMIT,
                inGroup,
                inGroupId: undefined,
            });
            proceduresGridUIStore.setParams({
                page: 0,
                limit: GROUP_PROCEDURES_LIMIT,
                inGroup,
                inGroupId: undefined,
            });
        }
    }

    const onRequestModalClose = () => {
        groupsUIStore.toggleGroupModalOpen(false);
        groupsUIStore.clearEditGroupId();
    }

    const onGroupDeleteRequest = () => {
        groupsUIStore.toggleGroupModalOpen(false);
        groupsUIStore.toggleDeleteConfirmationModalOpened(true);
    }

    useEffect(() => {
        if (!isPhoneLayout) {
            setGroupsDrawerOpen(false);
        }
    }, [isPhoneLayout]);

    useEffect(() => {
        let hasInitializedFilters = false;

        const dispose = reaction(
            () => ({
                isLoading: groupsUIStore.isLoading,
                massEdit: groupsUIStore.massEdit,
                selectedGroupId: groups.selectedGroup?.id,
            }),
            ({ isLoading, massEdit, selectedGroupId }) => {
                if (isLoading) {
                    return;
                }

                if (!hasInitializedFilters) {
                    hasInitializedFilters = true;

                    return;
                }

                if (!massEdit && selectedGroupId) {
                    setGroupFilter(2, selectedGroupId);

                    return;
                }

                setGroupFilter(0);
            },
            { fireImmediately: true },
        );

        return () => {
            dispose();
        };
    }, [groups, groupsUIStore, proceduresGridUIStore, usersGridUIStore]);

    return (
        <>
            <Observer>
                {() =>{
                    return(
                        <div className={styles.mainWrapper}>
                            {!isPhoneLayout && (
                                <div className={styles.sideWrapper}>
                                    <GroupsHeading />
                                    <GroupsList />
                                </div>
                            )}
                            <div className={styles.centerWrapper}>
                                {isPhoneLayout && (
                                    <button
                                        type="button"
                                        className={styles.groupsDrawerTrigger}
                                        onClick={() => setGroupsDrawerOpen(true)}
                                    >
                                        <MenuIcon fontSize="small" />
                                        <span className={styles.groupsDrawerTriggerName}>
                                            {groupsUIStore.selectedGroup ? groupsUIStore.selectedGroup.name : 'Groups'}
                                        </span>
                                    </button>
                                )}
                                <div className={styles.headerWrapper}>
                                    <GroupHeader />
                                </div>
                                {isTabletLayout && (
                                    <div className={styles.tabsContainer}>
                                        <button
                                            className={styles.contentTab}
                                            data-active={selectedTab === 'users'}
                                            onClick={() => setSelectedTab('users')}
                                            type="button"
                                        >
                                        Users
                                        </button>
                                        <button
                                            className={styles.contentTab}
                                            data-active={selectedTab === 'procedures'}
                                            onClick={() => setSelectedTab('procedures')}
                                            type="button"
                                        >
                                        Procedures
                                        </button>
                                    </div>
                                )}
                                <div className={styles.contentWrapper}>
                                    {groupsUIStore.selectedGroup ? (
                                        <>
                                            {(!isTabletLayout || selectedTab === 'users') && (
                                                <div className={styles.contentPanel}>
                                                    <UsersList />
                                                </div>
                                            )}
                                            {(!isTabletLayout || selectedTab === 'procedures') && (
                                                <div className={styles.contentPanel}>
                                                    <ProceduresList />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        !groupsUIStore.isLoading && <NoGroupSelected />
                                    )}
                                </div>
                            </div>
                            {isPhoneLayout && (
                                <Drawer
                                    anchor="left"
                                    open={groupsDrawerOpen}
                                    onClose={() => setGroupsDrawerOpen(false)}
                                >
                                    <div className={styles.groupsDrawerContent}>
                                        <GroupsHeading />
                                        <GroupsList onGroupSelected={() => setGroupsDrawerOpen(false)} />
                                    </div>
                                </Drawer>
                            )}
                            {
                                groupsUIStore.groupModalOpened && (
                                    <GroupModal
                                        isOpen={true}
                                        onRequestClose={onRequestModalClose}
                                        onGroupDeleteRequest={onGroupDeleteRequest}
                                        groupId={groupsUIStore.groupToEditId}
                                    />
                                )
                            }
                        </div>
                    )
                }}
            </Observer>
        </>
    );
};
